const CTX_KEY = 'cli_login.ctx';
export const EXPECTING_CLI_AUTH_REDIRECT_KEY = 'cli_login.expecting_auth_redirect';

export const CTX_TTL_MS = 15 * 60 * 1000;
const BASE64URL_SHA256_LENGTH = 43;
const BASE64URL_RE = /^[A-Za-z0-9_-]+$/;

export interface CliLoginParams {
  callback: string;
  state: string;
  code_challenge: string;
}

export interface CliLoginContext extends CliLoginParams {
  startedAt: number;
}

export type CliLoginContextResult =
  | { status: 'valid'; ctx: CliLoginContext }
  | { status: 'expired' }
  | null;

export const validateCallback = (value: string | null | undefined): boolean => {
  if (!value) return false;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'http:') return false;
  if (parsed.hostname !== '127.0.0.1' && parsed.hostname !== 'localhost') return false;
  if (!parsed.port) return false;
  return true;
};

export const validateCodeChallenge = (value: string | null | undefined): boolean => {
  if (!value) return false;
  if (value.length !== BASE64URL_SHA256_LENGTH) return false;
  return BASE64URL_RE.test(value);
};

const validateState = (value: string | null | undefined): boolean => !!value && value.length > 0;

export const urlHasAnyCliParam = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.has('cli_callback') || params.has('cli_state') || params.has('code_challenge');
};

export const readCliLoginParamsFromUrl = (): CliLoginParams | null => {
  const params = new URLSearchParams(window.location.search);
  const callback = params.get('cli_callback');
  const state = params.get('cli_state');
  const code_challenge = params.get('code_challenge');
  if (!validateCallback(callback) || !validateState(state) || !validateCodeChallenge(code_challenge)) {
    return null;
  }
  return { callback: callback!, state: state!, code_challenge: code_challenge! };
};

export const persistCliLoginContext = (params: CliLoginParams): void => {
  if (!validateCallback(params.callback) || !validateState(params.state) || !validateCodeChallenge(params.code_challenge)) {
    return;
  }
  const ctx: CliLoginContext = { ...params, startedAt: Date.now() };
  window.sessionStorage.setItem(CTX_KEY, JSON.stringify(ctx));
};

// Does NOT delete ctx on TTL expiry — CliConsent distinguishes "timed out" from
// "no flow in progress" via the three-state result (HLD §2.11).
export const getCliLoginContext = (): CliLoginContextResult => {
  const raw = window.sessionStorage.getItem(CTX_KEY);
  if (!raw) return null;
  let ctx: CliLoginContext;
  try {
    ctx = JSON.parse(raw) as CliLoginContext;
  } catch {
    return null;
  }
  if (
    !ctx ||
    typeof ctx.startedAt !== 'number' ||
    !validateCallback(ctx.callback) ||
    !validateState(ctx.state) ||
    !validateCodeChallenge(ctx.code_challenge)
  ) {
    return null;
  }
  if (Date.now() - ctx.startedAt >= CTX_TTL_MS) {
    return { status: 'expired' };
  }
  return { status: 'valid', ctx };
};

export const clearCliLoginContext = (): void => {
  window.sessionStorage.removeItem(CTX_KEY);
  window.sessionStorage.removeItem(EXPECTING_CLI_AUTH_REDIRECT_KEY);
};

export type CliRedirectPayload =
  | { code: string; state: string }
  | { error: 'access_denied'; state: string };

export const CLI_RESULT_PATH = '/login/cli-result';

export const buildCliRedirectUrl = (
  ctx: Pick<CliLoginContext, 'callback'>,
  payload: CliRedirectPayload,
  organisationId?: string | null,
): string => {
  const params = new URLSearchParams();
  const next = 'code' in payload
    ? `${CLI_RESULT_PATH}?status=success`
    : `${CLI_RESULT_PATH}?status=denied`;
  if ('code' in payload) {
    params.set('code', payload.code);
  } else {
    params.set('error', payload.error);
  }
  params.set('state', payload.state);
  params.set('next', next);
  if (organisationId) {
    params.set('organisation_id', organisationId);
  }
  return `${ctx.callback}?${params.toString()}`;
};
